import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Check, XCircle, Upload, AlertTriangle, RotateCcw, DollarSign, ChevronRight, FileText, Clock, TrendingUp, Download } from "lucide-react";
import api, { BASE } from "../api";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, SearchSelect, Av, Pr, Pagination, usePagination } from "./index";
import { notify, confirmDialog, promptDialog } from "../notify";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

const STATUS_COLOR = {
  Pending: K.a, AwaitingAck: K.pu, Active: K.bl,
  Completed: K.g, Rejected: K.r, Reversed: K.t3,
};
const STATUSES = ['Pending', 'AwaitingAck', 'Active', 'Completed', 'Rejected', 'Reversed'];

const fmtMoney = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');
const fmtDate = (d) => d ? fmtDMY(d) : '—';

export const DeductionsManager = ({ role, companies = [], offices = [], employees = [], initialOpenId = null }) => {
  // Raising a deduction is a Settings → Permissions switch (HR Corner → Manual Deductions).
  const perms = usePermissions(true);
  const canManage = perms.can('hrcorner', 'deductions', 'raise_deduction', 'edit');
  const [rows, setRows] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [openId, setOpenId] = useState(initialOpenId);
  // A notification's deep link can arrive after this component already
  // mounted (tab switch within HR Corner) — pick up a later id too.
  useEffect(() => { if (initialOpenId) setOpenId(initialOpenId); }, [initialOpenId]);

  // Filters
  const [fEmp, setFEmp]         = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fOffice, setFOffice]   = useState('');
  const [fType, setFType]       = useState('');
  const [fStatus, setFStatus]   = useState('');
  const [fMonth, setFMonth]     = useState('');

  const load = () => {
    const p = new URLSearchParams();
    if (fEmp)     p.set('employee_id',    fEmp);
    if (fCompany) p.set('company_id',     fCompany);
    if (fOffice)  p.set('office_id',      fOffice);
    if (fType)    p.set('deduction_type', fType);
    if (fStatus)  p.set('status',         fStatus);
    if (fMonth)   p.set('month',          fMonth);
    api.get('/api/deductions?' + p.toString())
      .then(d => setRows(Array.isArray(d?.rows) ? d.rows : []))
      .catch(() => setRows([]));
  };
  useEffect(load, [fEmp, fCompany, fOffice, fType, fStatus, fMonth]); // eslint-disable-line

  useEffect(() => {
    api.get('/api/deductions/settings').then(setSettings).catch(() => setSettings(null));
  }, []);

  const monthOptions = useMemo(() => {
    const opts = [{ v: '', l: 'All months' }];
    const now = new Date();
    for (let i = -3; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      opts.push({ v, l: d.toLocaleString('default', { month: 'long' }) + ' ' + d.getFullYear() });
    }
    return opts;
  }, []);

  const totals = useMemo(() => {
    const rr = rows || [];
    const active = rr.filter(r => r.status === 'Active' || r.status === 'AwaitingAck');
    const totalRecorded = rr.reduce((s, r) => s + parseFloat(r.total_amount || 0), 0);
    const recoveredTotal = rr.reduce((s, r) => s + (parseFloat(r.total_amount || 0) - parseFloat(r.remaining_amount || 0)), 0);
    const runningInstallments = rr.filter(r => r.status === 'Active' && r.recovery_mode === 'installment');
    const monthlyRunRate = runningInstallments.reduce((s, r) => s + parseFloat(r.monthly_amount || 0), 0);
    const nextMonth = runningInstallments
      .map(r => r.next_installment_month).filter(Boolean).sort()[0];
    return {
      count: rr.length,
      activeCount: active.length,
      outstandingTotal: active.reduce((s, r) => s + parseFloat(r.remaining_amount || 0), 0),
      totalRecorded,
      recoveredTotal,
      recoveredPct: totalRecorded > 0 ? Math.round(recoveredTotal / totalRecorded * 1000) / 10 : 0,
      monthlyRunRate,
      nextMonth,
    };
  }, [rows]);

  const fmtMonth = (ym) => {
    if (!ym) return null;
    const [y, m] = ym.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString('default', { month: 'short', year: 'numeric' });
  };

  const anyFilterActive = fEmp || fCompany || fOffice || fType || fStatus || fMonth;
  const resetFilters = () => { setFEmp(''); setFCompany(''); setFOffice(''); setFType(''); setFStatus(''); setFMonth(''); };

  const exportCsv = () => {
    const header = ["Employee", "Employee ID", "Type", "Reason", "Total", "Remaining", "Status", "Raised"];
    const csvRows = (rows || []).map(r => [r.employee_name || r.employee_id, r.employee_id, r.deduction_type, r.reason, r.total_amount, r.remaining_amount, r.status, fmtDate(r.created_at)]);
    const csv = [header, ...csvRows].map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `deductions-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const rowPage = usePagination(rows || [], 10);

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-t3 uppercase tracking-wide">Total Recorded</span>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: K.pg, color: K.p }}><DollarSign size={15} /></div>
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <div className="text-[22px] font-extrabold text-t0 tracking-tight">{fmtMoney(totals.totalRecorded)}</div>
            <span className="text-[11px] font-semibold text-t2 bg-bg2 px-[6px] py-[2px] rounded">{totals.count} case{totals.count === 1 ? '' : 's'}</span>
          </div>
          <p className="text-[11px] text-t3 mt-1">All deductions raised to date</p>
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-t3 uppercase tracking-wide">Outstanding Balance</span>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: K.ab, color: K.a }}><Clock size={15} /></div>
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <div className="text-[22px] font-extrabold tracking-tight" style={{ color: K.a }}>{fmtMoney(totals.outstandingTotal)}</div>
            <span className="text-[11px] font-semibold px-[6px] py-[2px] rounded" style={{ background: K.ab, color: K.a }}>{totals.activeCount} Active</span>
          </div>
          <p className="text-[11px] text-t3 mt-1">Pending payroll recovery</p>
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-t3 uppercase tracking-wide">Total Recovered</span>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: K.gb, color: K.g }}><Check size={15} /></div>
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <div className="text-[22px] font-extrabold text-t0 tracking-tight">{fmtMoney(totals.recoveredTotal)}</div>
            <span className="text-[11px] font-semibold text-g">{totals.recoveredPct}%</span>
          </div>
          <Pr value={totals.recoveredPct} c={K.g} h={5} />
        </div>
        <div className="bg-s rounded-2xl border border-b0 p-4 shadow-[0_2px_10px_rgba(15,23,42,0.05)]">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-t3 uppercase tracking-wide">Current Run Rate</span>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: K.pub, color: K.pu }}><TrendingUp size={15} /></div>
          </div>
          <div className="flex items-baseline justify-between mt-2">
            <div className="text-[22px] font-extrabold text-t0 tracking-tight">{fmtMoney(totals.monthlyRunRate)}<span className="text-[11px] font-normal text-t3">/mo</span></div>
            {totals.nextMonth && <span className="text-[11px] font-semibold px-[6px] py-[2px] rounded" style={{ background: K.pub, color: K.pu }}>Next: {fmtMonth(totals.nextMonth)}</span>}
          </div>
          <p className="text-[11px] text-t3 mt-1">Deducted from active installment plans</p>
        </div>
      </div>

      <Cd style={{ marginBottom: 12 }}>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Employee</div>
            <Sl bg="bg-pb" value={fEmp} onChange={setFEmp}
                options={[{ v: '', l: 'All employees' }, ...employees.filter(e => e.st === 'Active').map(e => ({ v: e.id, l: `${e.nm} · ${e.id}` }))]} />
          </div>
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Company</div>
            <Sl bg="bg-pb" value={fCompany} onChange={setFCompany}
                options={[{ v: '', l: 'All companies' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} />
          </div>
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Location / Centre</div>
            <Sl bg="bg-pb" value={fOffice} onChange={setFOffice}
                options={[{ v: '', l: 'All locations' }, ...offices.map(o => ({ v: String(o.id), l: (o.name || o.nm) + (o.city ? ` — ${o.city}` : '') }))]} />
          </div>
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Type</div>
            <Sl bg="bg-pb" value={fType} onChange={setFType}
                options={[{ v: '', l: 'All types' }, ...(settings?.deduction_types || []).map(t => ({ v: t, l: t }))]} />
          </div>
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Status</div>
            <Sl bg="bg-pb" value={fStatus} onChange={setFStatus}
                options={[{ v: '', l: 'All statuses' }, ...STATUSES.map(s => ({ v: s, l: s }))]} />
          </div>
          <div>
            <div className="text-[11px] font-bold text-t3 uppercase mb-[3px]">Month</div>
            <Sl bg="bg-pb" value={fMonth} onChange={setFMonth} options={monthOptions} />
          </div>
        </div>
        <div className="pt-3 mt-3 border-t border-b0 flex flex-wrap items-center justify-between gap-2 text-[14px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-t3 font-medium">Summary Snapshot:</span>
            <span className="inline-flex items-center px-2 py-[2px] rounded-full bg-bg2 text-t1 font-semibold text-[12px]">Total Records: <b className="ml-1 text-t0">{totals.count}</b></span>
            <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[12px] font-semibold" style={{ background: K.pg, color: K.p }}>Active / In Progress: <b className="ml-1">{totals.activeCount}</b></span>
            <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[12px] font-semibold" style={{ background: K.ab, color: K.a }}>Outstanding: <b className="ml-1">{fmtMoney(totals.outstandingTotal)}</b></span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {perms.can('hrcorner', 'deductions', 'manage_deductions', 'view') && <button onClick={exportCsv} className="text-[14px] font-semibold text-p bg-transparent border-0 cursor-pointer flex items-center gap-1"><Download size={13} /> Export</button>}
            {anyFilterActive && <button onClick={resetFilters} className="text-[14px] font-semibold text-p bg-transparent border-0 cursor-pointer">Reset Filters</button>}
            {canManage && <Bt icon={Plus} s="sm" onClick={() => setShowAdd(true)}>Add Deduction</Bt>}
          </div>
        </div>
      </Cd>

      {rows === null ? (
        <Cd><div className="text-center p-6 text-t3 text-[14px]">Loading…</div></Cd>
      ) : rows.length === 0 ? (
        <Cd>
          <div className="text-center p-10 text-t3 text-[14px]">
            <DollarSign size={22} className="mx-auto mb-2 opacity-40" />
            No deductions match those filters yet.
          </div>
        </Cd>
      ) : (
        <div className="rounded-2xl border border-b0 overflow-hidden shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[14px]">
              <thead><tr className="bg-bg2/80 text-t3 text-[11px] uppercase font-bold tracking-wider">
                <th className="px-3 py-[11px] text-left border-b border-b0">Employee</th>
                <th className="px-3 py-[11px] text-left border-b border-b0">Type</th>
                <th className="px-3 py-[11px] text-left border-b border-b0">Reason</th>
                <th className="px-3 py-[11px] text-right border-b border-b0">Total</th>
                <th className="px-3 py-[11px] text-right border-b border-b0">Remaining</th>
                <th className="px-3 py-[11px] text-left border-b border-b0">Recovery Schedule</th>
                <th className="px-3 py-[11px] text-left border-b border-b0">Status</th>
                <th className="px-3 py-[11px] text-left border-b border-b0">Raised</th>
                <th className="px-3 py-[11px] border-b border-b0"></th>
              </tr></thead>
              <tbody className="divide-y divide-b0">
                {rowPage.pageData.map(r => {
                  const paidAmount = parseFloat(r.total_amount || 0) - parseFloat(r.remaining_amount || 0);
                  const paidCount = r.recovery_mode === 'installment' && r.monthly_amount > 0 ? Math.round(paidAmount / r.monthly_amount) : null;
                  const schedPct = r.recovery_mode === 'installment' && r.num_installments > 0 ? Math.min(paidCount / r.num_installments * 100, 100) : null;
                  return (
                    <tr key={r.id} className="group hover:bg-sh transition-colors">
                      <td className="px-3 py-[10px] text-t0">
                        <div className="flex items-center gap-2">
                          <Av nm={r.employee_name || r.employee_id} sz={28} />
                          <div className="min-w-0">
                            <div className="font-bold truncate">{r.employee_name || r.employee_id}</div>
                            <div className="text-[11px] text-t3 truncate">{r.employee_id} · {r.office_city || r.office_name || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-[10px]"><Bg c={STATUS_COLOR[r.status] || K.p} sm dot>{r.deduction_type}</Bg></td>
                      <td className="px-3 py-[10px] text-t2 max-w-[220px] truncate" title={r.reason}>{r.reason}</td>
                      <td className="px-3 py-[10px] text-right text-t0 font-bold font-mono">{fmtMoney(r.total_amount)}</td>
                      <td className="px-3 py-[10px] text-right font-mono">
                        <span className={parseFloat(r.remaining_amount) > 0 ? 'text-a font-bold' : 'text-g font-bold'}>{fmtMoney(r.remaining_amount)}</span>
                      </td>
                      <td className="px-3 py-[10px] text-t2">
                        {r.recovery_mode === 'installment' ? (
                          <>
                            <div className="font-semibold text-t1">{fmtMoney(r.monthly_amount)}/mo × {r.num_installments}</div>
                            {schedPct !== null && (
                              <div className="flex items-center gap-[6px] mt-1">
                                <div className="w-[70px]"><Pr value={schedPct} c={schedPct >= 100 ? K.g : K.p} h={5} /></div>
                                <span className="text-[11px] font-bold text-t3">{paidCount}/{r.num_installments}</span>
                              </div>
                            )}
                          </>
                        ) : 'One-time'}
                      </td>
                      <td className="px-3 py-[10px]"><Bg c={STATUS_COLOR[r.status] || K.t3} sm dot>{r.status}</Bg></td>
                      <td className="px-3 py-[10px] text-t3 text-[12px]">
                        {fmtDate(r.created_at)}
                        {r.created_by_name && <div>by {r.created_by_name}</div>}
                      </td>
                      <td className="px-3 py-[10px] text-right">
                        <button onClick={() => setOpenId(r.id)}
                          className="bg-transparent border-0 text-p cursor-pointer flex items-center gap-1 text-[14px] ml-auto font-semibold">
                          Open <ChevronRight size={11} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination total={rows.length} page={rowPage.page} pageSize={rowPage.pageSize} onPageChange={rowPage.onPageChange} onPageSizeChange={rowPage.onPageSizeChange} />
        </div>
      )}

      {showAdd && (
        <AddDeductionModal
          settings={settings}
          employees={employees}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
      {openId && (
        <DeductionDetailDrawer
          id={openId} role={role}
          onClose={() => setOpenId(null)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
};

const AddDeductionModal = ({ settings, employees, onClose, onCreated }) => {
  const [form, setForm] = useState({
    employee_id: '', deduction_type: (settings?.deduction_types || [])[0] || '',
    reason: '', total_amount: '',
    recovery_mode: 'one_time',
    monthly_amount: '', num_installments: '',
    start_month: (() => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + 1); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); })(),
    remarks: '',
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputCls = "w-full px-3 py-2 bg-bg2 border border-b0 rounded-[10px] text-[14px] text-t0 outline-none";

  const amount = parseFloat(form.total_amount) || 0;
  const t = settings?.approval_thresholds || {};
  const band = amount <= (t.hr_only?.max || 0) ? 'HR alone'
             : amount <= (t.manager?.max || 0) ? 'Manager'
             : amount <= (t.centre_head?.max || 0) ? 'Centre Head'
             : 'CHRO / Admin';
  const ackAbove = settings?.employee_ack_required_above || 0;
  const willNeedAck = amount > ackAbove;

  const submit = async () => {
    setErr('');
    if (!form.employee_id) return setErr('Pick an employee');
    if (!(amount > 0)) return setErr('Total amount must be greater than 0');
    if (!form.deduction_type) return setErr('Deduction type is required');
    if (!form.reason.trim()) return setErr('Reason is required');
    if (form.recovery_mode === 'installment') {
      const m = parseFloat(form.monthly_amount), n = parseInt(form.num_installments);
      if (!(m > 0)) return setErr('Monthly amount must be > 0');
      if (!(n > 0)) return setErr('Installments must be > 0');
      if (Math.abs(m * n - amount) > 1) return setErr(`Installments ${m} × ${n} = ${m * n} doesn't match total ${amount}`);
    }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v != null) fd.append(k, v); });
      if (file) fd.append('doc', file);
      await api.upload('/api/deductions', fd);
      onCreated();
    } catch (e) { setErr(e.message || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1047px] max-h-[92vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="text-[16px] font-extrabold text-t0">Raise Deduction</div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          {err && <div className="px-3 py-2 mb-3 rounded-lg text-[14px] font-semibold bg-rb text-r border border-rd">{err}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[14px] text-t3 mb-1">Employee <span className="text-r">*</span></div>
              <SearchSelect
                value={form.employee_id}
                onChange={v => upd('employee_id', v)}
                placeholder="Select employee"
                searchPlaceholder="Search by name or ID…"
                options={employees.filter(e => e.st === 'Active').map(e => ({
                  v: e.id,
                  l: e.nm,
                  sub: `${e.id}${e.rl?.t || e.rl?.title ? ' · ' + (e.rl.t || e.rl.title) : ''}`,
                }))}
              />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Deduction Type <span className="text-r">*</span></div>
              <select className={inputCls} value={form.deduction_type} onChange={e => upd('deduction_type', e.target.value)}>
                {(settings?.deduction_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Reason <span className="text-r">*</span></div>
              <textarea className={inputCls + " min-h-[60px]"} value={form.reason} onChange={e => upd('reason', e.target.value)} placeholder="Broken laptop screen recovered from insurance …" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Total Amount (₹) <span className="text-r">*</span></div>
              <input type="number" className={inputCls} value={form.total_amount} onChange={e => upd('total_amount', e.target.value)} placeholder="8000" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Recovery Mode</div>
              <select className={inputCls} value={form.recovery_mode} onChange={e => upd('recovery_mode', e.target.value)}>
                <option value="one_time">One-time (single payroll cycle)</option>
                <option value="installment">Installment (over multiple months)</option>
              </select>
            </div>
            {form.recovery_mode === 'installment' && <>
              <div>
                <div className="text-[14px] text-t3 mb-1">Monthly Amount (₹)</div>
                <input type="number" className={inputCls} value={form.monthly_amount} onChange={e => upd('monthly_amount', e.target.value)} placeholder="2000" />
              </div>
              <div>
                <div className="text-[14px] text-t3 mb-1">Number of Installments</div>
                <input type="number" className={inputCls} value={form.num_installments} onChange={e => upd('num_installments', e.target.value)} placeholder="4" />
              </div>
            </>}
            <div>
              <div className="text-[14px] text-t3 mb-1">{form.recovery_mode === 'installment' ? 'Start Month' : 'Recovery Month'}</div>
              <input type="month" className={inputCls} value={form.start_month} onChange={e => upd('start_month', e.target.value)} />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-1">Supporting Document (optional)</div>
              <label className="flex items-center gap-2 px-3 py-2 bg-bg2 border border-dashed border-b1 rounded-[10px] cursor-pointer hover:border-p">
                <Upload size={14} className="text-t2" />
                <span className="text-[14px] text-t1 truncate">{file?.name || 'Choose PDF / image'}</span>
                <input type="file" accept=".pdf,image/*" className="hidden"
                  onChange={e => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div className="sm:col-span-2">
              <div className="text-[14px] text-t3 mb-1">Remarks (internal)</div>
              <textarea className={inputCls + " min-h-[50px]"} value={form.remarks} onChange={e => upd('remarks', e.target.value)} placeholder="Any HR notes…" />
            </div>
          </div>

          {amount > 0 && settings && (
            <div className="mt-4 p-3 rounded-lg bg-bg2 border border-b0 text-[14px] text-t2">
              <div className="font-semibold text-t0 mb-1">Approval preview</div>
              <div>Amount routes to <b className="text-p">{band}</b> for approval.</div>
              <div className="mt-1">
                {willNeedAck
                  ? <>Employee must acknowledge before payroll picks it up — auto-approves after <b>{settings.employee_ack_auto_approve_hours}h</b> if silent.</>
                  : <>No acknowledgment required (below the {fmtMoney(ackAbove)} threshold).</>}
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <Bt v="success" onClick={submit} disabled={busy} icon={Check}>{busy ? 'Saving…' : 'Raise Deduction'}</Bt>
            <Bt v="secondary" onClick={onClose} disabled={busy}>Cancel</Bt>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeductionDetailDrawer = ({ id, role, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { can: pcan } = usePermissions(true);
  const canManage = pcan('hrcorner', 'deductions', 'manage_deductions', 'edit');
  const canApprove = ['admin', 'hr_head', 'centre_head', 'centre_hr'].includes(role);

  const load = () => api.get('/api/deductions/' + id).then(setData).catch(() => setData(null));
  // Statement-body arrow so useEffect doesn't misinterpret the returned
  // Promise as a cleanup function (see EmailSettingsTab / IntakeInviteCard).
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const act = async (path, body) => {
    setBusy(true);
    try { await api.put('/api/deductions/' + id + path, body || {}); load(); onChanged?.(); }
    catch (e) { notify(e.message || 'Failed'); }
    finally { setBusy(false); }
  };

  const doReject = async () => {
    const reason = await promptDialog('Rejection reason:', { title: 'Reject deduction', multiline: true, variant: 'error', okLabel: 'Reject' });
    if (!reason || !reason.trim()) return;
    await act('/reject', { reason });
  };

  const doReverse = async () => {
    if (!(await confirmDialog('Reverse this deduction? A positive line will appear on the next payslip.'))) return;
    await act('/reverse');
  };

  if (!data) {
    return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onClick={onClose}>
      <Cd style={{ padding: 20 }}>Loading…</Cd>
    </div>;
  }

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl w-full max-w-[1047px] max-h-[92vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 z-10">
          <div className="min-w-0">
            <div className="text-[16px] font-extrabold text-t0 truncate">{data.employee_name} · {data.deduction_type}</div>
            <div className="text-[14px] text-t3 flex items-center gap-2 flex-wrap mt-1">
              <Bg c={STATUS_COLOR[data.status] || K.t3} sm dot>{data.status}</Bg>
              <span>{fmtMoney(data.total_amount)} total</span>
              <span>·</span>
              <span className={parseFloat(data.remaining_amount) > 0 ? 'text-a' : 'text-g'}>{fmtMoney(data.remaining_amount)} remaining</span>
              {data.created_by_name && <><span>·</span><span>Raised by {data.created_by_name} on {fmtDate(data.created_at)}</span></>}
            </div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <RO label="Employee" value={`${data.employee_name} · ${data.employee_id}`} />
            <RO label="Company / Centre" value={`${data.company_name || '—'} · ${data.office_city || data.office_name || '—'}`} />
            <RO label="Recovery" value={data.recovery_mode === 'installment'
              ? `${fmtMoney(data.monthly_amount)}/mo × ${data.num_installments} installments`
              : 'One-time'} />
            <RO label="Window" value={`${data.next_installment_month || '—'} → ${data.end_month || '—'}`} />
            {data.approved_by_name && <RO label="Approved by" value={`${data.approved_by_name} on ${fmtDate(data.approved_at)}`} />}
            {data.dispute_reason && <RO label="Employee Dispute" value={<span className="text-r">{data.dispute_reason}</span>} />}
            {data.rejection_reason && <RO label="Rejection Reason" value={<span className="text-r">{data.rejection_reason}</span>} />}
          </div>

          <div className="p-3 rounded-lg bg-bg2 border border-b0 mb-4">
            <div className="text-[14px] text-t3 uppercase mb-1">Reason</div>
            <div className="text-[14px] text-t0">{data.reason}</div>
            {data.remarks && <div className="text-[14px] text-t3 italic mt-2">Remarks: {data.remarks}</div>}
          </div>

          {data.supporting_doc_path && (
            <a href={BASE + '/' + data.supporting_doc_path} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1 text-[14px] text-p mb-4">
              <FileText size={12} /> View supporting document
            </a>
          )}

          {data.recoveries?.length > 0 && (
            <div className="mb-4">
              <div className="text-[14px] font-bold text-t0 mb-2">Recovery Trail</div>
              <div className="rounded border border-b0 overflow-hidden">
                <table className="w-full border-collapse text-[14px]">
                  <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
                    <th className="px-2 py-[6px] text-left">Period</th>
                    <th className="px-2 py-[6px] text-right">Amount</th>
                    <th className="px-2 py-[6px] text-left">Recorded</th>
                  </tr></thead>
                  <tbody>
                    {data.recoveries.map(r => (
                      <tr key={r.period} className="border-b border-b0 last:border-b-0">
                        <td className="px-2 py-[6px] text-t0">{r.period}</td>
                        <td className="px-2 py-[6px] text-right font-mono text-t1">{fmtMoney(r.amount)}</td>
                        <td className="px-2 py-[6px] text-t3">{fmtDate(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap pt-3 border-t border-b0">
            {data.status === 'Pending' && canApprove && (
              <Bt v="success" s="sm" icon={Check} onClick={() => act('/approve')} disabled={busy}>Approve</Bt>
            )}
            {(data.status === 'Pending' || data.status === 'AwaitingAck') && canApprove && (
              <Bt v="danger" s="sm" icon={XCircle} onClick={doReject} disabled={busy}>Reject</Bt>
            )}
            {['Active', 'AwaitingAck', 'Completed'].includes(data.status) && canManage && (
              <Bt v="secondary" s="sm" icon={RotateCcw} onClick={doReverse} disabled={busy}>Reverse</Bt>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const RO = ({ label, value }) => (
  <div className="bg-bg2 border border-b0 rounded p-2">
    <div className="text-[12px] text-t3 uppercase">{label}</div>
    <div className="text-[14px] text-t0 mt-[2px]">{value || '—'}</div>
  </div>
);

// Employee-facing card — Additional Deductions on MyProfile. Shows all
// deductions for the current user with Acknowledge / Dispute buttons on
// AwaitingAck rows.
export const MyDeductionsCard = () => {
  const [rows, setRows] = useState(null);

  const load = () => api.get('/api/deductions/my').then(d => setRows(Array.isArray(d?.rows) ? d.rows : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const acknowledge = async (id) => {
    if (!(await confirmDialog('Acknowledge this deduction? It will be applied on the next payroll cycle.'))) return;
    try { await api.put('/api/deductions/' + id + '/acknowledge'); load(); }
    catch (e) { notify(e.message || 'Failed'); }
  };

  const dispute = async (id) => {
    const reason = await promptDialog("Why are you disputing this deduction? Your reason will go to HR for review.", { title: 'Raise a dispute', multiline: true, variant: 'warn', okLabel: 'Submit dispute' });
    if (!reason || !reason.trim()) return;
    try { await api.put('/api/deductions/' + id + '/dispute', { reason }); load(); }
    catch (e) { notify(e.message || 'Failed'); }
  };

  if (rows === null) return <div className="text-[14px] text-t3">Loading…</div>;
  if (rows.length === 0) return null;

  return (
    <div>
      <div className="text-[14px] font-bold text-t0 mb-2 flex items-center gap-2">
        <DollarSign size={12} color={K.a} /> Additional Deductions
      </div>
      <div className="p-3 rounded-lg bg-bg2 border border-b0">
        <div className="text-[14px] text-t3 mb-3">
          Deductions raised by HR — asset recovery, advance repayment, policy penalties, etc. Rows in <b>Awaiting Ack</b> need your acknowledgment before payroll picks them up; you can also dispute if you disagree.
        </div>
        <div className="rounded border border-b0 overflow-hidden bg-bg1">
          <table className="w-full border-collapse text-[14px]">
            <thead><tr className="bg-bg2 text-t3 text-[12px] uppercase">
              <th className="px-2 py-[6px] text-left">Type</th>
              <th className="px-2 py-[6px] text-left">Reason</th>
              <th className="px-2 py-[6px] text-right">Total</th>
              <th className="px-2 py-[6px] text-right">Remaining</th>
              <th className="px-2 py-[6px] text-left">Status</th>
              <th className="px-2 py-[6px]"></th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-b0 last:border-b-0">
                  <td className="px-2 py-[6px] text-t0 font-semibold">{r.deduction_type}</td>
                  <td className="px-2 py-[6px] text-t2 max-w-[280px] truncate" title={r.reason}>{r.reason}</td>
                  <td className="px-2 py-[6px] text-right font-mono text-t1">{fmtMoney(r.total_amount)}</td>
                  <td className="px-2 py-[6px] text-right font-mono">
                    <span className={parseFloat(r.remaining_amount) > 0 ? 'text-a' : 'text-g'}>{fmtMoney(r.remaining_amount)}</span>
                  </td>
                  <td className="px-2 py-[6px]"><Bg c={STATUS_COLOR[r.status] || K.t3} sm dot>{r.status}</Bg></td>
                  <td className="px-2 py-[6px] text-right whitespace-nowrap">
                    {r.status === 'AwaitingAck' && (
                      <>
                        <Bt v="success" s="xs" icon={Check} onClick={() => acknowledge(r.id)}>Acknowledge</Bt>
                        <Bt v="danger" s="xs" icon={AlertTriangle} onClick={() => dispute(r.id)}>Dispute</Bt>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
